/**
 * One definition of what a course costs.
 *
 * The rule — a promo applies only when it is below the list price — was written
 * out longhand in four places and omitted in four others, which is how the
 * catalogue reprice ended up showing N12,000 on the card, N20,000 in the
 * schema.org block, and N20,000 to Paystack for the same course. The gate lives
 * here now so a promo cannot be honoured on one surface and ignored on the next.
 *
 * The server is still the authority on the amount charged
 * (`priceForCourse` in backend/src/services/enrollment.ts) — this mirrors it so
 * the page shows what the charge will be, not so the browser decides it.
 */

interface PricedCourse {
  price?: number;
  price_ngn?: number;
  promoPrice?: number;
}

const listPriceNgn = (course: PricedCourse): number =>
  Number(course.price_ngn ?? course.price) || 0;

/** True when `promoPrice` is a real discount rather than a leftover markup. */
const promoApplies = (course: PricedCourse): boolean => {
  const promo = Number(course.promoPrice);
  return Number.isFinite(promo) && promo >= 0 && promo < listPriceNgn(course);
};

/** The NGN price a buyer will be charged, promo included when it is one. */
export const effectivePriceNgn = (course: PricedCourse | null | undefined): number => {
  if (!course) return 0;
  return promoApplies(course) ? Number(course.promoPrice) : listPriceNgn(course);
};

/**
 * The price to strike through, or null when there is nothing to strike — a
 * course at list price has no "was", and a stale promo is not a "was" either.
 */
export const strikethroughPriceNgn = (
  course: PricedCourse | null | undefined
): number | null => (course && promoApplies(course) ? listPriceNgn(course) : null);
